/**@jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { Popover, Box } from '@mui/material';
import React from 'react';
import { useAtomValue } from 'jotai';
import { DT_ROADMAP_LINK, DT_API_DOCS_LINK, DT_HELP_DOCS_LINK } from '../../../constants';
import DeveloperTerminalIcon from '../../../icons/icons-revamp/developer_terminal';
import FeatureRequestIcon from '../../../icons/icons-revamp/feature_request';
import HelpIcon from '../../../icons/icons-revamp/help';
import TrainingIcon from '../../../icons/icons-revamp/training';
import { openPopup } from '../../../utils/popup';
import { QSTypography } from '../../atoms';
import ContactSupportOptions from './TrainingPopover';
import SupportAccessSection from './SupportAccessSection';
import { ROUTES } from '../../../constants/routes';
import { userProfileAtom } from '../../../states/user';
import { UserProvider } from '../../../../modules/home/v1/typings';

const GetHelpPopover = ({
  helpPopoverAnchorEl,
  handleCloseHelpPopover,
  hideDTDetails,
}: {
  helpPopoverAnchorEl: HTMLElement | null;
  handleCloseHelpPopover: () => void;
  hideDTDetails: boolean;
}) => {
  const userProfile = useAtomValue(userProfileAtom);
  const isSupportAgent = userProfile?.provider === UserProvider.SUPPORT_AGENT;
  const trainingLinkData = `${ROUTES.DASHBOARD}?training=true`;
  const handleHelpDocsClick = () => {
    openPopup(DT_HELP_DOCS_LINK, '_blank');
    handleCloseHelpPopover();
  };
  return (
    <Popover
      open={Boolean(helpPopoverAnchorEl)}
      anchorEl={helpPopoverAnchorEl}
      onClose={handleCloseHelpPopover}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      slotProps={{
        paper: {
          sx: {
            mt: '8px',
            minWidth: '200px',
            borderRadius: '8px',
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.10), 0 4px 6px 0 rgba(0, 0, 0, 0.05)',
            border: '1px solid rgba(30, 41, 59, 0.14)',
          },
        },
      }}
    >
      {!hideDTDetails && !isSupportAgent && <SupportAccessSection />}
      <ContactSupportOptions handleClose={handleCloseHelpPopover} />
      <Box>
        {!hideDTDetails && trainingLinkData && (
          <Box
            component="button"
            onClick={() => {
              window.open(trainingLinkData, '_blank');
              handleCloseHelpPopover();
            }}
            css={css`
              width: 100%;
              display: flex;
              align-items: center;
              gap: 8px;
              padding: 12px 16px;
              height: 44px;
              background: transparent;
              border: none;
              cursor: pointer;
              &:hover {
                background-color: rgba(0, 0, 0, 0.04);
              }
            `}
          >
            <TrainingIcon size={20} color="#7A7A7A" />
            <QSTypography
              css={css`
                font-size: 14px;
                font-weight: 500;
                color: #2d2d2d;
              `}
            >
              Training Videos
            </QSTypography>
          </Box>
        )}

        {hideDTDetails ? null : (
          <Box
            component="button"
            onClick={handleHelpDocsClick}
            css={css`
              width: 100%;
              display: flex;
              align-items: center;
              gap: 8px;
              padding: 12px 16px;
              height: 44px;
              background: transparent;
              border: none;
              cursor: pointer;
              &:hover {
                background-color: rgba(0, 0, 0, 0.04);
              }
            `}
          >
            <HelpIcon size={20} color="#7A7A7A" />
            <QSTypography
              css={css`
                font-size: 14px;
                font-weight: 500;
                color: #2d2d2d;
              `}
            >
              Learn Center
            </QSTypography>
          </Box>
        )}

        {hideDTDetails ? null : (
          <Box
            component="button"
            onClick={() => {
              // TODO: Add API Docs link
              openPopup(DT_API_DOCS_LINK, '_blank');
              handleCloseHelpPopover();
            }}
            css={css`
              width: 100%;
              display: flex;
              align-items: center;
              gap: 8px;
              padding: 12px 16px;
              height: 44px;
              background: transparent;
              border: none;
              cursor: pointer;
              &:hover {
                background-color: rgba(0, 0, 0, 0.04);
              }
            `}
          >
            <DeveloperTerminalIcon size={20} color="#7A7A7A" />
            <QSTypography
              css={css`
                font-size: 14px;
                font-weight: 500;
                color: #2d2d2d;
              `}
            >
              API Docs
            </QSTypography>
          </Box>
        )}

        {!hideDTDetails && (
          <Box
            component="button"
            onClick={() => {
              openPopup(DT_ROADMAP_LINK, '_blank');
              handleCloseHelpPopover();
            }}
            css={css`
              width: 100%;
              display: flex;
              align-items: center;
              gap: 8px;
              padding: 12px 16px;
              height: 44px;
              background: transparent;
              border: none;
              cursor: pointer;
              &:hover {
                background-color: rgba(0, 0, 0, 0.04);
              }
            `}
          >
            <FeatureRequestIcon size={20} color="#7A7A7A" />
            <QSTypography
              css={css`
                font-size: 14px;
                font-weight: 500;
                color: #2d2d2d;
              `}
            >
              Request a feature
            </QSTypography>
          </Box>
        )}
      </Box>
    </Popover>
  );
};

export default GetHelpPopover;
